// Báo thiếu / hỏng hàng hoàn (§2.6.5, GĐ7): sau khi BEST trả hàng về (708), người bán có vài ngày
// (`returnClaimDays`, ShopTrack đặt) để báo; ShopTrack khiếu nại BEST trong hạn. Hạn báo có sẵn
// trên đơn: `order.money.returnClaimDeadline`.
// POST /reconcile/return-claims { orderId, kind, note } → { ok, deadline }.
// Lỗi: return_claim.expired (details.deadline) · return_claim.not_returned.

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { ApiError, post } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

type ClaimKind = 'missing' | 'damaged' | 'wrong_item'

/** "dd/mm/yyyy HH:mm" */
export function formatDeadline(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${formatDate(d)} ${d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false })}`
}

/** Đã quá hạn báo chưa (không có hạn = chưa biết, coi như còn hạn — máy chủ kiểm lại). */
export function claimExpired(deadline: string | null | undefined, now = Date.now()): boolean {
  if (!deadline) return false
  const t = new Date(deadline).getTime()
  return Number.isFinite(t) && now > t
}

const KINDS: { value: ClaimKind; label: string }[] = [
  { value: 'missing', label: 'Thiếu hàng' },
  { value: 'damaged', label: 'Hàng hư hỏng' },
  { value: 'wrong_item', label: 'Sai hàng' },
]

export function ReturnClaimDialog({
  orderId,
  waybillCode,
  deadline,
  onClose,
  onSent,
}: {
  orderId: string
  waybillCode: string | null
  /** Hạn báo (returnClaimDeadline của đơn). */
  deadline?: string | null
  onClose: () => void
  onSent: (deadline: string | null) => void
}) {
  const [kind, setKind] = useState<ClaimKind | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const noteOk = note.trim().length >= 5

  const send = useMutation({
    mutationFn: () => post<{ ok: boolean; deadline: string | null }>('/reconcile/return-claims', { orderId, kind, note: note.trim() }),
    onSuccess: (r) => onSent(r?.deadline ?? null),
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'return_claim.expired') {
        const d = (e.details as { deadline?: string } | undefined)?.deadline
        setError(`${e.message}${d ? ` (hạn báo đã hết lúc ${formatDeadline(d)})` : ''}.`)
        return
      }
      if (e instanceof ApiError && e.code === 'return_claim.not_returned') {
        setError(`${e.message}.`)
        return
      }
      setError((e as Error).message || 'Không gửi được')
    },
  })

  return (
    <Dialog open onOpenChange={(o) => !o && !send.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="pr-8">
          <DialogTitle>Báo thiếu / hỏng hàng hoàn</DialogTitle>
          <DialogDescription>
            {waybillCode ? <>Vận đơn <span className="font-mono">{waybillCode}</span>. </> : null}
            {deadline && (
              <span className="mt-1.5 block font-medium text-ink">
                Hạn báo: <span className="tabular-nums">{formatDeadline(deadline)}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form
          id="return-claim"
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!kind || !noteOk) return
            setError(null)
            send.mutate()
          }}
        >
          <fieldset className="space-y-2">
            <legend className="mb-1.5 text-sm text-inkSoft">Tình trạng</legend>
            {KINDS.map((k) => (
              <label
                key={k.value}
                className={cn(
                  'motion-state flex cursor-pointer gap-3 rounded-lg border p-3 text-sm',
                  kind === k.value ? 'border-accent bg-accent/5' : 'border-rule hover:bg-paperSoft',
                )}
              >
                <input type="radio" name="claim-kind" className="mt-1 accent-accent" checked={kind === k.value} onChange={() => setKind(k.value)} />
                <span>
                  <span className="block font-medium text-ink">{k.label}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <label className="block space-y-1.5">
            <span className="text-sm text-inkSoft">Mô tả</span>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={4} placeholder="VD: thiếu 1 áo size M" />
            {note.length > 0 && !noteOk && <span className="block text-xs text-warn">Mô tả ít nhất 5 ký tự.</span>}
          </label>

          {error && (
            <p className="flex items-start gap-2 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger" role="alert">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}
        </form>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onClose} disabled={send.isPending}>
            Hủy
          </Button>
          <Button type="submit" form="return-claim" disabled={!kind || !noteOk || send.isPending}>
            {send.isPending ? 'Đang gửi…' : 'Gửi'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Dòng xác nhận sau khi đã gửi (trong phiên này). */
export function ReturnClaimSent({ deadline }: { deadline: string | null }) {
  return (
    <p className="flex items-start gap-2 rounded-lg bg-ok/10 px-3 py-2 text-sm text-ink">
      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-ok" />
      <span>
        Đã gửi báo cáo.
        {deadline ? ` Hạn báo: ${formatDeadline(deadline)}` : ''}
      </span>
    </p>
  )
}
